import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { userApi, authApi } from '../services/api'
import {
  updateGlobalAuthState,
  getGlobalAuthState,
  subscribeToAuthState,
  type AuthState
} from '../services/authState'
import type { UserProfile as User } from '@treu/shared'

// Re-export for consumers
export { updateGlobalAuthState, type AuthState }

// Query keys for user data
export const userKeys = {
  all: ['user'] as const,
  profile: () => [...userKeys.all, 'profile'] as const,
}

// Session check promise to prevent duplicate API calls
let sessionCheckPromise: Promise<void> | null = null

export function useAuth() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [authState, setAuthState] = useState<AuthState>(getGlobalAuthState())

  // Subscribe to global state changes
  useEffect(() => {
    return subscribeToAuthState(setAuthState)
  }, [])

  // Only check session once globally, not per hook instance
  useEffect(() => {
    if (sessionCheckPromise || getGlobalAuthState().status === 'authenticated') {
      return // Already checked or checking
    }

    const checkSession = async () => {
      try {
        const response = await userApi.getProfile()
        const newState: AuthState = { status: 'authenticated', user: response.data }
        updateGlobalAuthState(newState)
      } catch {
        const newState: AuthState = { status: 'unauthenticated' }
        updateGlobalAuthState(newState)
      } finally {
        sessionCheckPromise = null
      }
    }

    sessionCheckPromise = checkSession()
  }, [])

  const refreshUser = async () => {
    try {
      const response = await userApi.getProfile()
      const newState: AuthState = { status: 'authenticated', user: response.data }
      updateGlobalAuthState(newState)
    } catch {
      const newState: AuthState = { status: 'unauthenticated' }
      updateGlobalAuthState(newState)
    }
  }

  // Helper functions for avatar display
  const getAvatarFallback = () => {
    if (authState.status !== 'authenticated' || !authState.user?.name) {
      return 'U'
    }
    return authState.user.name.charAt(0).toUpperCase()
  }

  const getDisplayName = () => {
    if (authState.status !== 'authenticated') {
      return 'User'
    }
    return authState.user?.name || authState.user?.email?.split('@')[0] || 'User'
  }

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: ({ provider, token, fullName }: { provider: 'google' | 'apple', token: string, fullName?: { givenName?: string; familyName?: string } }) =>
      authApi.socialLogin(provider, token, fullName),
    onSuccess: (response) => {
      const userData = response.data?.user

      if (response.success && userData) {
        const newState: AuthState = { status: 'authenticated', user: userData }
        updateGlobalAuthState(newState)
        navigate('/dashboard', { replace: true })
      }
    }
  })

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      const newState: AuthState = { status: 'unauthenticated' }
      updateGlobalAuthState(newState)
      queryClient.clear()
      navigate('/login', { replace: true })
    },
    onError: () => {
      // Logout anyway even if API call fails
      const newState: AuthState = { status: 'unauthenticated' }
      updateGlobalAuthState(newState)
      queryClient.clear()
      navigate('/login', { replace: true })
    }
  })

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (updates: Partial<User>) => userApi.updateProfile(updates),
    onSuccess: (response) => {
      const newState: AuthState = { status: 'authenticated', user: response.data }
      updateGlobalAuthState(newState)
    },
  })

  // Upload avatar mutation
  const uploadAvatarMutation = useMutation({
    mutationFn: (file: File) => userApi.uploadAvatar(file),
    onSuccess: (response) => {
      const currentState = getGlobalAuthState()
      if (currentState.status === 'authenticated') {
        const updatedUser = { ...currentState.user, profilePicture: response.data.profilePicture }
        const newState: AuthState = { status: 'authenticated', user: updatedUser }
        updateGlobalAuthState(newState)
      }
      // Don't call refreshUser here to avoid unnecessary API call
    },
  })

  return {
    // Auth state
    authState,
    user: authState.status === 'authenticated' ? authState.user : null,
    isLoading: authState.status === 'loading',

    // Auth actions
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    refreshUser,

    // Loading states
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,

    // Profile actions
    updateProfile: updateProfileMutation.mutateAsync,
    uploadAvatar: uploadAvatarMutation.mutateAsync,
    isUpdatingProfile: updateProfileMutation.isPending,
    isUploadingAvatar: uploadAvatarMutation.isPending,

    // Helper functions
    getAvatarFallback,
    getDisplayName,

    // Computed values
    hasProfilePicture: authState.status === 'authenticated' && !!authState.user?.profilePicture,
    isAuthenticated: authState.status === 'authenticated',
  }
}